import React from 'react';
import { Clock, CheckCircle, AlertCircle, XCircle, FileText, Brain, Users, Sparkles, X } from 'lucide-react';
import { useProcessing } from '../contexts/ProcessingContext';
import { useTranslation } from "react-i18next";

const ProcessingStatusCard = ({ job, onDismiss, compact = false }) => {
  const { t } = useTranslation();
  const { getStageInfo, cancelProcessing } = useProcessing();
  const stageInfo = getStageInfo(job.stage);

  const getIcon = () => {
    switch (job.stage) {
      case 'uploaded':
        return <FileText className="w-4 h-4" />;
      case 'parsing':
        return <FileText className="w-4 h-4" />;
      case 'generating':
        return <Brain className="w-4 h-4" />;
      case 'extracting_entities':
        return <Users className="w-4 h-4" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4" />;
      case 'failed':
        return <XCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const getElapsedTime = () => {
    if (!job.startTime) return '';
    const elapsed = Math.floor((Date.now() - job.startTime) / 1000);
    if (elapsed < 60) return `${elapsed}s`;
    return `${Math.floor(elapsed / 60)}m ${elapsed % 60}s`;
  };

  if (compact) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className={`w-2 h-2 rounded-full ${stageInfo.color}`}></div>
        <span className="text-sm text-gray-700">{stageInfo.label}</span>
        <div className="flex-1 bg-gray-200 rounded-full h-1.5">
          <div 
            className={`h-1.5 rounded-full transition-all duration-500 ${stageInfo.color}`}
            style={{ width: `${job.progress}%` }}
          ></div>
        </div>
        <span className="text-xs text-gray-500">{job.progress}%</span>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 mb-3">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`p-1.5 rounded-full ${stageInfo.color} bg-opacity-10`}>
            <div className={`${stageInfo.color.replace('bg-', 'text-')}`}>
              {getIcon()}
            </div>
          </div>
          <div>
            <h4 className="font-medium text-gray-900">{t('processing.title')}</h4>
            <p className="text-sm text-gray-500">ID: {job.documentId.slice(-8)}</p>
          </div>
        </div>
        
        {job.stage === 'completed' || job.stage === 'failed' ? (
          <button
            onClick={() => onDismiss?.(job.documentId)}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            title={t('common.delete')}
          >
            <XCircle className="w-4 h-4" />
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <div className="text-xs text-gray-500">{getElapsedTime()}</div>
            <button
              onClick={() => cancelProcessing(job.documentId)}
              className="text-red-400 hover:text-red-600 transition-colors p-1 rounded hover:bg-red-50"
              title={t('common.cancel')}
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>

      {/* Progress Bar */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm font-medium text-gray-700">{stageInfo.label}</span>
          <span className="text-sm text-gray-500">{job.progress}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className={`h-2 rounded-full transition-all duration-500 ${stageInfo.color}`}
            style={{ width: `${job.progress}%` }}
          ></div>
        </div>
      </div>

      {/* Stage Details */}
      <div className="text-xs text-gray-600">
        {job.stage === 'uploaded' && (
          <p>✅ {t('processing.status.uploaded')}</p>
        )}
        {job.stage === 'parsing' && (
          <p>📖 {t('processing.status.parsing')}</p>
        )}
        {job.stage === 'generating' && (
          <p>🧠 {t('processing.status.generating')}</p>
        )}
        {job.stage === 'extracting_entities' && (
          <p>👥 {t('processing.status.extracting_entities')}</p>
        )}
        {job.stage === 'completed' && (
          <p className="text-green-600">🎉 {t('processing.status.success_msg')}</p>
        )}
        {job.stage === 'failed' && (
          <p className="text-red-600">❌ {t('processing.status.error_msg', { error: job.error || t('common.try_again') })}</p>
        )}
      </div>
    </div>
  );
};

const ProcessingStatusBar = ({ asFeedPost = false }) => {
  const { getActiveJobs, getAllJobs, removeJob } = useProcessing();
  const activeJobs = getActiveJobs();
  const allJobs = getAllJobs();
  
  // Show recently completed jobs for 5 seconds
  const recentCompletedJobs = allJobs.filter(job => 
    (job.stage === 'completed' || job.stage === 'failed') && 
    job.lastUpdate && 
    (Date.now() - job.lastUpdate) < 5000
  );

  const jobsToShow = [...activeJobs, ...recentCompletedJobs];

  if (jobsToShow.length === 0) {
    return null;
  }

  // If used as feed post, return inline content
  if (asFeedPost) {
    return (
      <div className="space-y-2">
        {jobsToShow.map((job) => (
          <ProcessingStatusCard
            key={job.documentId}
            job={job}
            onDismiss={removeJob}
            compact={activeJobs.length > 1}
          />
        ))}
      </div>
    );
  }

  // Original fixed positioning for backward compatibility
  return (
    <div className="fixed top-16 left-1/2 transform -translate-x-1/2 w-full max-w-md z-40 px-4">
      <div className="space-y-2">
        {jobsToShow.map((job) => (
          <ProcessingStatusCard
            key={job.documentId}
            job={job}
            onDismiss={removeJob}
            compact={activeJobs.length > 1}
          />
        ))}
      </div>
    </div>
  );
};

export default ProcessingStatusBar;